import { Group } from "iconoir-react"
import { Globe, Images, Loader2 } from "lucide-react"
import { ChatInput } from "features/chat/components/ChatInput"
import { useChatStore } from "state/store/chat/use-chat-store"
import { Attachment } from "/features/chat/types/chat";
import { useNavigate, useParams } from "react-router-dom";
import { ChatInputButton } from "../chat/chat";
import { useEffect, useMemo, useState } from "react";
import { ButtonIds, initialFeaturesState } from "features/chat/types/chat";
import { useConfig } from "hooks/use-config";
import { ButtonTypes, Alignment } from "pages/chat/chat";
import { useTranslation } from "react-i18next";
import { useIsMobile } from "hooks/use-mobile";
import { useFetchProject, useFetchProjectConversations } from "features/projects/hooks/use-fetch-project";
import { ConversationActions } from "features/projects/components/conversation-actions";
import { useToast } from "../../hooks/use-toast";
import { trackEvent } from "analytics/tracker";
import { EVENTS, InitialPromptType } from "analytics/events";



import { useInviteAssistant } from "features/group-chat/hooks/use-invite";
import { InvitationPayload } from "features/group-chat/services/group-services";
import { DEFAULT_MAIN_CHAT_AGENT_ID } from "utils/constants/chat-constants";
import { useModelStore } from "state/store/chat/use-model-store";
import { useFetchProjectFiles } from "features/projects/hooks/use-projects";
import type { IntegrationSource } from "services/sse-service";
import AddFilesModal from "features/projects/components/add-files-modal";
import AddInstructionModal from "features/projects/components/add-instruction-modal";
import ProjectActionsDropdown from "features/projects/components/project-actions-dropdown";
import { ProjectFolderIcon } from "components/blocks/layout/project-folder-icon";
import projectFolderDark from "assets/projectFolderDark.svg";
import projectFolderWhite from "assets/projectfolderWhite.svg";
import { ProjectActionCards } from "features/projects/components/project-action-cards";
import { cn } from "lib/utils";
import { CHAT_MESSAGES_SCROLL_CLASS } from "constant/chat-layout";

const formatConversationCardDate = (dateInput: string) => {
    const date = new Date(dateInput);
    if (Number.isNaN(date.getTime())) return '';

    const month = date.toLocaleString('en-US', { month: 'long' });
    const day = date.getDate();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${month} ${day} | ${hours}:${minutes}`;
};

const GRID_COLUMNS_DESKTOP = 3;
const GRID_COLUMNS_MOBILE = 2;
const MIN_GRID_SLOTS_DESKTOP = 6;
const MIN_GRID_SLOTS_MOBILE = 4;

const getPlaceholderCount = (itemCount: number, columns: number) => {
    const minSlots = columns === GRID_COLUMNS_MOBILE ? MIN_GRID_SLOTS_MOBILE : MIN_GRID_SLOTS_DESKTOP;
    const totalSlots = Math.max(minSlots, Math.ceil(itemCount / columns) * columns);
    return totalSlots - itemCount;
};

const conversationCardBaseClassName =
    'flex w-full min-w-0 cursor-pointer flex-col items-start justify-between self-stretch rounded-lg p-3 text-grey-700 transition-colors dark:text-true-white sm:p-4 h-[164px] sm:h-[220px] lg:h-[266.5px]';

/** Shared centered column for header / scroll body / chat. */
const PROJECT_COLUMN_CLASS = 'mx-auto w-full max-w-[829px] px-4 sm:px-6 md:px-8';
/**
 * Same stable scrollbar gutter on every full-width row so the centered columns
 * share identical left/right edges (matches CHAT_MESSAGES_SCROLL_CLASS).
 */
const PROJECT_SCROLLBAR_GUTTER_CLASS = '[scrollbar-gutter:stable]';
/**
 * Middle column: keep left padding aligned with header/chat, add extra right
 * padding so the content right edge matches (thin scrollbar is narrower than
 * the stable gutter reserved on the fixed rows).
 */
const PROJECT_SCROLL_COLUMN_CLASS =
    'mx-auto flex w-full max-w-[829px] flex-col gap-4 pb-[33px] pl-4 pr-[calc(1rem+0.375rem)] sm:pl-6 sm:pr-[calc(1.5rem+0.375rem)] md:pl-8 md:pr-[calc(2rem+0.2rem)]';

export default function ProjectPage() {

    const sendMessageToProject = useChatStore((state) => state.sendMessageToProject);
    const setActiveConversationId = useChatStore((state) => state.setActiveConversationId);
    // const sendGroupChatStream = useChatStore((state) => state.sendGroupChatStream);
    // const temporaryConversation = useChatStore((state) => state.temporaryConversation);
    // const replaceConversationId = useChatStore((state) => state.replaceConversationId);
    // const sendMessageToProject = useChatStore((state) => state.sendMessageToProject);
    // const replyMessage = useChatStore((state) => state.replyMessage);
    const navigate = useNavigate();
    const { toast } = useToast();
    const { projectId } = useParams();
    // const [inputButtons, setInputButtons] = useState<ChatInputButton[]>([]);
    const [openFilesModal, setOpenFilesModal] = useState(false);
    const [isAddInstructionModalOpen, setIsAddInstructionModalOpen] = useState(false)

    const [isFeatureSelected, setIsFeatureSelected] =
        useState<Record<ButtonIds, boolean>>(initialFeaturesState);
    const { configurationSettings } = useConfig();
    const { t } = useTranslation();
    const isMobile = useIsMobile();
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [hoveredConversations, setHoveredConversations] = useState<Record<string, boolean>>({});
    const [gridColumns, setGridColumns] = useState(GRID_COLUMNS_MOBILE);
    const inviteAssistantMutation = useInviteAssistant();
    const selectedModel = useModelStore.getState().selectedModel;

    const sidebarProjectsList = useChatStore((state) => state.sidebarProjectsList);
    const updateProjectItems = useChatStore((state) => state.updateProjectItems);
    const setSidebarProjectsList = useChatStore((state) => state.setSidebarProjectsList);
    const projectFromStore = sidebarProjectsList.find((project) => project.id === projectId);

    const { data: fetchedProject, refetch: refetchProject, isError: isFetchingProjectMetadataError, isFetching: isFetchingProject } = useFetchProject(
        projectId ?? '',
        // {
        //     enabled: !projectFromStore,
        // }
    );

    const { data: fetchedProjectConversations, isLoading: isFetchingProjectConversations, isFetchedAfterMount } = useFetchProjectConversations(
        projectId ?? '',
        {
            enabled: projectFromStore && !projectFromStore.items?.length,
        }
    );

    const [selectedSources, setSelectedSources] = useState<string[]>([]);
    const [isSourcesToolActive, setIsSourcesToolActive] = useState<boolean>(false);
    const [selectedTools, setSelectedTools] = useState<string[]>([]);

    useEffect(() => {
        if (!fetchedProject?.id || !projectId || fetchedProject.id !== projectId) return;

        const exists = useChatStore.getState().sidebarProjectsList.some((p) => p.id === fetchedProject.id);
        if (!exists) {
            const currentProjects = useChatStore.getState().sidebarProjectsList;
            setSidebarProjectsList([...currentProjects, fetchedProject]);
        }
    }, [fetchedProject?.id, projectId, setSidebarProjectsList]);

    useEffect(() => {
        if (!isFetchedAfterMount || !fetchedProjectConversations || !projectId) return;
        updateProjectItems(projectId, fetchedProjectConversations);
    }, [fetchedProjectConversations, updateProjectItems, isFetchedAfterMount, projectId])

    useEffect(() => {
        if (!isDropdownOpen) {
            setHoveredConversations({});
        }
    }, [isDropdownOpen]);

    // Detach from the previous chat so this landing composer is not bound to a
    // conversation that may still be streaming in the background.
    useEffect(() => {
        setActiveConversationId(null);
    }, [projectId, setActiveConversationId]);

    useEffect(() => {
        const mediaQuery = window.matchMedia('(min-width: 1024px)');
        const updateGridColumns = () => {
            setGridColumns(mediaQuery.matches ? GRID_COLUMNS_DESKTOP : GRID_COLUMNS_MOBILE);
        };

        updateGridColumns();
        mediaQuery.addEventListener('change', updateGridColumns);
        return () => mediaQuery.removeEventListener('change', updateGridColumns);
    }, []);

    const inputButtons = useMemo<ChatInputButton[]>(() => [
        {
            id: ButtonIds.SEARCH,
            label: t('SEARCH'),
            icon: <Globe size={16} />,
            onClick: () => {
                setIsFeatureSelected((prev) => ({
                    ...initialFeaturesState,
                    [ButtonIds.SEARCH]: !prev[ButtonIds.SEARCH],
                }));
            },
            type: isMobile ? ButtonTypes.ICON : ButtonTypes.BUTTON,
            alignment: Alignment.LEFT,
            isDisabled: isFeatureSelected[ButtonIds.CREATE_IMAGE] || selectedSources.length > 0,
            isSelected: isFeatureSelected[ButtonIds.SEARCH],
        },
        {
            id: ButtonIds.CREATE_IMAGE,
            label: t('CREATE_IMAGE'),
            icon: <Images size={16} />,
            onClick: () => {
                setIsFeatureSelected((prev) => ({
                    ...initialFeaturesState,
                    [ButtonIds.CREATE_IMAGE]: !prev[ButtonIds.CREATE_IMAGE],
                }));
            },
            type: isMobile ? ButtonTypes.ICON : ButtonTypes.BUTTON,
            alignment: Alignment.LEFT,
            isDisabled: selectedSources.length > 0,
            isSelected: isFeatureSelected[ButtonIds.CREATE_IMAGE],
        }
    ], [isMobile, isFeatureSelected, t, selectedSources.length]);

    useEffect(() => {
        if (isFetchingProjectMetadataError) {
            toast({
                variant: "destructive",
                title: t('NOT_FOUND'),
                description: t('PROJECT_NOT_FOUND'),
            });
            navigate('/chat');
        }
    }, [isFetchingProjectMetadataError, toast, navigate, t]);


    const sendMessage = async (
        content: string,
        files: Attachment[],
        sources?: IntegrationSource[],
        _mentions?: unknown,
        tools?: string[],
    ) => {
        setActiveConversationId(null);
        if (!projectId)
            return;

        // const { temporaryConversationId } = temporaryConversation(content, files, isFeatureSelected);
        // // console.log('temporaryConversationId in sendMessage', temporaryConversationId)
        // navigate(`/chat`, {
        //     state: {
        //         from: 'project',
        //         preservedFeatures: isFeatureSelected
        //     }
        // });

        const payload: InvitationPayload = {
            invitationType: 'ADD_USER',
            agentId: DEFAULT_MAIN_CHAT_AGENT_ID,
            assistantId: selectedModel?.id,
        };
        const response = await inviteAssistantMutation.mutateAsync(payload);

        if (response?.conversationId) {
            const newConversationId = response?.conversationId;

            trackEvent(EVENTS.NEW_CHAT_STARTED(newConversationId, InitialPromptType.TEXT));

            const integrationSources = sources ?? [];
            await sendMessageToProject(
                content,
                configurationSettings,
                projectId,
                newConversationId,
                files,
                isFeatureSelected,
                integrationSources,
                tools ?? selectedTools,
            );

            navigate(`/chat/${newConversationId}/${projectId}`, {
                state: {
                    from: 'project',
                    preservedFeatures: isFeatureSelected,
                    preservedSources: selectedSources,
                    preservedSourcesToolActive: isSourcesToolActive,
                    preservedTools: tools ?? selectedTools,
                }
            });
        }
    };

    const { data: projectFiles } = useFetchProjectFiles({ projectId: projectId ?? '', page: 1, pageSize: 10 });

    const project = projectId ? projectFromStore || fetchedProject : null;
    const conversationItems = project?.items ?? [];
    const placeholderCount = useMemo(
        () => getPlaceholderCount(conversationItems.length, gridColumns),
        [conversationItems.length, gridColumns],
    );

    if (!projectId) return (
        <div className="absolute inset-0 z-10 flex items-start justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
        </div>
    )

    const handleOpenInstructionModal = () => {
        if (fetchedProject) {
            setIsAddInstructionModalOpen(true);
        } else {
            refetchProject();
            setIsAddInstructionModalOpen(true);
        }
    };

    return (
        <div className="flex h-full min-h-0 w-full flex-1 flex-col overflow-hidden pt-4">
            {/* Header — same scrollbar-gutter as scroll + chat so columns align */}
            <div className={cn('w-full shrink-0 overflow-y-auto', PROJECT_SCROLLBAR_GUTTER_CLASS)}>
                <header className={cn(PROJECT_COLUMN_CLASS, 'flex items-center justify-between gap-3 pb-8 lg:pb-[60px]')}>
                    <div className="flex h-8 min-w-0 flex-1 items-center gap-2 sm:w-[287px] sm:flex-none">
                        <div className="flex size-8 shrink-0 items-center justify-center">
                            <ProjectFolderIcon size="lg" color={project?.color} />
                        </div>
                        <h1 className="min-w-0 max-w-full truncate whitespace-nowrap font-sans text-[24px] font-medium not-italic leading-[28.8px] tracking-normal text-grey-900 dark:text-true-white">
                            {project?.name}
                        </h1>
                    </div>

                    <div className="flex shrink-0 items-center justify-end gap-6">
                        <ProjectActionsDropdown fetchedProject={project} />
                        <button
                            type="button"
                            className="inline-flex h-11 shrink-0 items-center gap-2 rounded-[100px] bg-black px-4 py-3"
                        >
                            <Group className="size-6 shrink-0 text-[#F9F9FF]" strokeWidth={1.5} />
                            <span className="text-center font-sans text-base font-medium not-italic leading-[20.8px] text-[#F9F9FF]">
                                {t('SHARE') || 'Share'}
                            </span>
                        </button>
                    </div>
                </header>
            </div>

            {/* Middle — chat scrollbar + stable gutter; content uses shared column */}
            <div
                className={cn('min-h-0 w-full flex-1 overflow-x-hidden', CHAT_MESSAGES_SCROLL_CLASS)}
                style={{
                    scrollbarWidth: 'thin',
                    scrollbarColor: 'hsl(var(--border) / 0.7) transparent',
                }}
            >
                <div className={PROJECT_SCROLL_COLUMN_CLASS}>
                    <ProjectActionCards
                        onAddInstruction={handleOpenInstructionModal}
                        onOpenFiles={() => setOpenFilesModal(true)}
                        totalFiles={projectFiles?.totalCount ?? 0}
                    />

                    {isFetchingProjectConversations && (
                        <div className="flex w-full items-center justify-center py-8">
                            <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
                        </div>
                    )}

                    <div className="flex flex-col gap-2">
                        <div className="grid grid-cols-2 gap-2 lg:grid-cols-3">
                            {conversationItems.map((item) => {
                                const lastConversation = item.Conversations?.[item.Conversations.length - 1];
                                const lastQuestion = lastConversation?.content || t('NO_QUESTION') || 'No question';
                                const isHovered = hoveredConversations[item.ConversationId];

                                return (
                                    <div
                                        key={item.ConversationId}
                                        onClick={() => navigate(`/chat/${item.ConversationId}/${projectId}`)}
                                        onMouseEnter={() =>
                                            setHoveredConversations((prev) => ({
                                                ...prev,
                                                [item.ConversationId]: true,
                                            }))
                                        }
                                        onMouseLeave={() =>
                                            !isDropdownOpen &&
                                            setHoveredConversations((prev) => ({
                                                ...prev,
                                                [item.ConversationId]: false,
                                            }))
                                        }
                                        className={cn(
                                            conversationCardBaseClassName,
                                            'group',
                                            isHovered
                                                ? 'border border-dashed border-grey-200 bg-grey-50 dark:border-border dark:bg-white/5'
                                                : 'bg-white dark:bg-background'
                                        )}
                                    >
                                        <div className="flex w-full flex-col gap-3">
                                            <div className="flex w-full items-center justify-between gap-2">
                                                <p className="line-clamp-1 min-w-0 flex-1 font-sans text-sm font-semibold leading-[1.2] text-grey-700 dark:text-true-white sm:max-w-[200px] sm:text-base">
                                                    {item.ConversationTitle}
                                                </p>
                                                <div
                                                    className="shrink-0"
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <ConversationActions
                                                        conversationId={item.ConversationId}
                                                        conversationTitle={item.ConversationTitle}
                                                        projectId={projectId}
                                                        projectName={project?.name ?? ''}
                                                        invalidateProject={true}
                                                        onOpenChange={(open) => setIsDropdownOpen(open)}
                                                    />
                                                </div>
                                            </div>
                                            <p className="line-clamp-2 w-full font-sans text-sm font-normal leading-[1.6] text-grey-700 dark:text-true-white sm:line-clamp-4 sm:text-base">
                                                {lastQuestion}
                                            </p>
                                        </div>
                                        <p className="w-full font-sans text-xs font-medium leading-[1.2] text-grey-700 dark:text-true-white">
                                            {formatConversationCardDate(item.LastUpdatedDate)}
                                        </p>
                                    </div>
                                );
                            })}

                            {Array.from({ length: placeholderCount }).map((_, index) => (
                                <div
                                    key={`placeholder-${index}`}
                                    className={cn(
                                        conversationCardBaseClassName,
                                        'cursor-default border border-dashed border-grey-200 dark:border-border'
                                    )}
                                    aria-hidden="true"
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Chat — same scrollbar-gutter as header + scroll so columns align */}
            <div className={cn('w-full shrink-0 overflow-y-auto', PROJECT_SCROLLBAR_GUTTER_CLASS)}>
                <div className={cn(PROJECT_COLUMN_CLASS, 'pb-4 sm:pb-8')}>
                    <ChatInput
                        onSendMessage={sendMessage}
                        isLoading={false}
                        inputButtons={inputButtons}
                        isCurrentResponseGenerating={false}
                        selectedSources={selectedSources}
                        onSourcesChange={setSelectedSources}
                        isFeatureSelected={isFeatureSelected}
                        isSourcesToolActive={isSourcesToolActive}
                        selectedTools={selectedTools}
                        onSelectedToolsChange={setSelectedTools}
                        onToggleTool={(toolId) => {
                            setIsFeatureSelected((prev) => ({
                                ...prev,
                                [toolId]: !prev[toolId],
                            }));
                        }}
                        onToggleSourcesTool={() => {
                            setIsSourcesToolActive(!isSourcesToolActive);
                        }}
                        isCancelOnGoing={false}
                    />
                </div>
            </div>

            <AddFilesModal projectName={project?.name ?? ''} isOpen={openFilesModal} setIsOpen={setOpenFilesModal} projectId={projectId ?? ''} />
            <AddInstructionModal isFetchingProject={isFetchingProject} fetchedProject={fetchedProject} isOpen={isAddInstructionModalOpen} setIsOpen={setIsAddInstructionModalOpen} projectId={projectId ?? ''} refetchProject={refetchProject} />
        </div>
    )
}
